/**
 * Mobile data-table → collapse-card engine.
 * Mutates table cell roles for CSS layout; always pair with `teardownMobileTableCards`.
 */

const ACTION_HEADERS = new Set(["action", "actions"]);

const FIELD_PRIORITY = [
  { test: (n) => n.includes("class") || n.includes("classroom"), score: 100 },
  { test: (n) => n.includes("type") || n.includes("category"), score: 90 },
  { test: (n) => n.includes("fee") || n.includes("amount") || n.includes("price") || n.includes("paid"), score: 85 },
  { test: (n) => n.includes("assignee") || n.includes("teacher"), score: 80 },
  { test: (n) => n.includes("parent") || n.includes("student") || n.includes("child") || n.includes("audience"), score: 75 },
  { test: (n) => n.includes("due") || n.includes("date") || n.includes("created") || n.includes("received"), score: 60 },
  { test: (n) => n.includes("qty") || n.includes("quantity") || n.includes("stock"), score: 55 },
  { test: (n) => n.includes("city") || n.includes("location"), score: 45 },
  { test: (n) => n === "id" || n.endsWith(" id") || n.includes("receipt"), score: 35 },
];

const ALWAYS_HIDE = [
  (n) => n.includes("description") || n.includes("desc"),
  (n) => n.includes("link") || n.includes("url"),
  (n) => n.includes("image") || n.includes("thumb"),
];

const CARET_SVG =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';

function headerText(th) {
  const clone = th.cloneNode(true);
  clone.querySelectorAll("input, button, svg").forEach((node) => node.remove());
  return (clone.textContent || "").replace(/\s+/g, " ").trim();
}

function isCheckCell(td) {
  return Boolean(td.querySelector('input[type="checkbox"], .data-check'));
}

function isActionCell(td) {
  return Boolean(
    td.querySelector(".data-action, .bw-row-actions, .bw-action-btn") ||
      (td.children.length === 1 &&
        td.querySelector("button, [role='button']") &&
        !td.querySelector(".data-person, .data-name, .data-pill, .data-status-trigger"))
  );
}

function isPrimaryCell(td) {
  return Boolean(
    td.querySelector(
      ".data-person, .data-name, .ad-title-cell, .act-title-cell, .hw-title, .notif-title-cell, .notif-title, .bw-avatar"
    ) ||
      td.classList.contains("hw-col-title") ||
      td.classList.contains("notif-col-title") ||
      td.classList.contains("inbox-col-title")
  );
}

function isContactCell(td) {
  return Boolean(td.querySelector(".data-contact"));
}

function isAvatarStackCell(td) {
  return Boolean(td.querySelector(".data-avatar-stack, .data-avatar-stack-empty"));
}

function isStatusCell(td) {
  return Boolean(
    td.querySelector(
      ".data-status-trigger, .data-pill.is-active, .data-pill.is-pending, .data-pill.is-inactive, .notif-badge-type, .inbox-read-toggle"
    )
  );
}

function isEmptyCell(td) {
  if (td.querySelector("img, svg, input, button, a, .data-pill, .data-contact, .data-avatar, .data-avatar-stack")) {
    const text = (td.textContent || "").replace(/\s+/g, " ").trim();
    if (
      td.querySelector(
        ".data-contact a[href], .data-pill, img, .data-avatar, .data-avatar-stack, input, button"
      )
    ) {
      return false;
    }
    return !text || text === "—" || text === "-";
  }
  const text = (td.textContent || "").replace(/\s+/g, " ").trim();
  return !text || text === "—" || text === "-";
}

function fieldScore(label) {
  const n = (label || "").toLowerCase();
  if (ALWAYS_HIDE.some((fn) => fn(n))) return -1;
  for (const item of FIELD_PRIORITY) {
    if (item.test(n)) return item.score;
  }
  return 40;
}

function unwrapExtras(td) {
  const value = td.querySelector(":scope > .data-card-detail-value");
  if (value) {
    while (value.firstChild) td.insertBefore(value.firstChild, value);
    value.remove();
  }
  td.querySelectorAll(
    ":scope > .data-card-meta-value, :scope > .data-card-meta-left, :scope > .data-card-ico, :scope > .data-card-detail-label, :scope > .data-card-subline"
  ).forEach((node) => {
    if (node.classList.contains("data-card-meta-value")) {
      while (node.firstChild) td.insertBefore(node.firstChild, node);
    }
    node.remove();
  });
}

function ensureDetailRow(td, label) {
  unwrapExtras(td);
  const value = document.createElement("span");
  value.className = "data-card-detail-value";
  while (td.firstChild) value.appendChild(td.firstChild);

  if (label) {
    const lab = document.createElement("span");
    lab.className = "data-card-detail-label";
    lab.textContent = label;
    td.appendChild(lab);
  }
  td.appendChild(value);
}

function setExpanded(tr, open) {
  tr.classList.toggle("is-expanded", open);
  const btn = tr.querySelector(".data-card-toggle");
  if (btn) {
    btn.setAttribute("aria-expanded", open ? "true" : "false");
    btn.setAttribute("aria-label", open ? "Collapse details" : "Expand details");
  }
}

function ensureToggleCell(tr) {
  let toggle = tr.querySelector(':scope > td[data-card-role="toggle"]');
  if (!toggle) {
    toggle = document.createElement("td");
    toggle.dataset.cardRole = "toggle";
    toggle.dataset.cardInjected = "1";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "data-card-toggle";
    btn.setAttribute("aria-label", "Expand details");
    btn.innerHTML = CARET_SVG;
    toggle.appendChild(btn);
    tr.appendChild(toggle);
  } else if (toggle !== tr.lastElementChild) {
    tr.appendChild(toggle);
  }
  const btn = toggle.querySelector(".data-card-toggle");
  if (btn) btn.innerHTML = CARET_SVG;
  return btn;
}

function bindCardToggle(tr) {
  if (tr.dataset.cardToggleBound === "1") return;
  tr.dataset.cardToggleBound = "1";

  tr.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (
      target.closest(
        "a, input, label, .data-action, .bw-row-actions, .bw-action-btn, .data-status-trigger, .data-contact, .ant-dropdown-trigger, [role='menu'], .data-card-toggle, .data-avatar-stack-item"
      )
    ) {
      return;
    }
    if (
      target.closest(
        'td[data-card-role="check"], td[data-card-role="action"], td[data-card-role="status"], td[data-card-role="avatars"]'
      )
    ) {
      return;
    }
    setExpanded(tr, !tr.classList.contains("is-expanded"));
  });
}

function resolveRole(td, index, label) {
  const normalized = (label || "").toLowerCase();
  if (isCheckCell(td) || (index === 0 && !label && td.querySelector("input"))) return "check";
  if (isActionCell(td) || ACTION_HEADERS.has(normalized)) return "action";
  if (isPrimaryCell(td)) return "primary";
  if (isContactCell(td)) return "icons";
  if (isAvatarStackCell(td)) return td.querySelector(".data-avatar-stack") ? "avatars" : "hide";
  if (isStatusCell(td) || normalized === "status") return "status";
  if (isEmptyCell(td)) return "hide";
  if (fieldScore(label) < 0) return "hide";
  return "detail";
}

function enhanceRow(tr, headers) {
  tr.classList.add("data-card-row");
  const wasExpanded = tr.classList.contains("is-expanded");
  const cells = Array.from(tr.children).filter((td) => td.dataset.cardRole !== "toggle");
  const detailCandidates = [];

  cells.forEach((td, index) => {
    const label = headers[index] || "";
    const role = resolveRole(td, index, label);

    td.dataset.cardRole = role;
    delete td.dataset.cardIcon;
    delete td.dataset.cardMeta;
    delete td.dataset.cardStripe;
    delete td.dataset.label;
    td.removeAttribute("data-label");
    unwrapExtras(td);

    if (role === "detail") {
      detailCandidates.push({ td, label, score: fieldScore(label), index });
    }
  });

  if (!tr.querySelector(':scope > td[data-card-role="primary"]')) {
    const candidate = detailCandidates[0];
    if (candidate) {
      candidate.td.dataset.cardRole = "primary";
      detailCandidates.shift();
    }
  }

  const primaries = Array.from(tr.querySelectorAll(':scope > td[data-card-role="primary"]'));
  if (primaries.length > 1) {
    primaries.forEach((cell, i) => {
      if (i === 0) return;
      const idx = cells.indexOf(cell);
      const label = headers[idx] || "";
      cell.dataset.cardRole = "detail";
      detailCandidates.push({ td: cell, label, score: fieldScore(label), index: idx });
    });
  }

  detailCandidates.sort((a, b) => b.score - a.score || a.index - b.index);

  detailCandidates.forEach(({ td, label }, i) => {
    td.dataset.cardRole = "detail";
    td.dataset.cardStripe = i % 2 === 0 ? "a" : "b";
    ensureDetailRow(td, label || "Info");
    if (i === 0) td.dataset.cardMeta = "start";
    else delete td.dataset.cardMeta;
  });

  tr.querySelectorAll(':scope > td[data-card-role="primary"]').forEach((td) => {
    td.querySelectorAll(":scope > .data-card-subline").forEach((n) => n.remove());
  });

  const iconCells = Array.from(tr.querySelectorAll(':scope > td[data-card-role="icons"]'));
  iconCells.forEach((td, i) => {
    if (isEmptyCell(td)) {
      td.dataset.cardRole = "hide";
      delete td.dataset.cardStripe;
      return;
    }
    const stripeIndex = detailCandidates.length + i;
    td.dataset.cardStripe = stripeIndex % 2 === 0 ? "a" : "b";
    ensureDetailRow(td, "Contact");
  });

  const toggleBtn = ensureToggleCell(tr);
  const toggleTd = tr.querySelector(':scope > td[data-card-role="toggle"]');
  const hasBody =
    detailCandidates.length > 0 ||
    Boolean(tr.querySelector(':scope > td[data-card-role="icons"]')) ||
    Boolean(tr.querySelector(':scope > td[data-card-role="action"]'));

  if (toggleTd) toggleTd.style.display = hasBody ? "" : "none";

  if (hasBody && toggleBtn && toggleBtn.dataset.bound !== "1") {
    toggleBtn.dataset.bound = "1";
    toggleBtn.addEventListener("click", (event) => {
      event.stopPropagation();
      setExpanded(tr, !tr.classList.contains("is-expanded"));
    });
  }

  if (hasBody) {
    bindCardToggle(tr);
    tr.style.cursor = "pointer";
    setExpanded(tr, wasExpanded);
  } else {
    tr.classList.remove("is-expanded");
    tr.style.cursor = "default";
  }
}

export function enhanceMobileTableCards(root) {
  if (!root) return;
  const tables = root.querySelectorAll("table.data-table");
  tables.forEach((table) => {
    const headers = Array.from(table.querySelectorAll("thead th")).map(headerText);
    table.classList.add("is-card-table");
    table.querySelectorAll("tbody tr").forEach((tr) => enhanceRow(tr, headers));
  });
}

export function teardownMobileTableCards(root) {
  if (!root) return;
  root.querySelectorAll("table.data-table").forEach((table) => {
    table.classList.remove("is-card-table");
    table.querySelectorAll("tbody tr").forEach((tr) => {
      tr.classList.remove("data-card-row", "is-expanded");
      tr.style.cursor = "";
      delete tr.dataset.cardToggleBound;

      Array.from(tr.querySelectorAll(':scope > td[data-card-role="toggle"][data-card-injected="1"]')).forEach(
        (td) => td.remove()
      );

      Array.from(tr.children).forEach((td) => {
        unwrapExtras(td);
        delete td.dataset.cardRole;
        delete td.dataset.cardStripe;
        delete td.dataset.cardMeta;
        delete td.dataset.cardIcon;
        delete td.dataset.cardInjected;
        td.style.display = "";
      });
    });
  });
}
